import React, { useMemo, useState } from 'react';
import { X, DollarSign, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import './CostAnalyticsModal.css';

const TEAL  = '#1D9E75';
const BLUE  = '#185FA5';
const RED   = '#E24B4A';
const AMBER = '#EF9F27';

const fmt = (n) => (parseFloat(n) || 0).toFixed(2);
const fmtInt = (n) => (parseInt(n) || 0).toLocaleString();

const CostTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="cost-tt">
      {label && <p className="cost-tt-label">{label}</p>}
      {payload.map((p, i) => (
        <div key={i} className="cost-tt-row">
          <span className="cost-tt-dot" style={{ background: p.fill || p.color }} />
          <span>{p.name}: <strong>₹{(p.value ?? 0).toFixed(2)}</strong></span>
        </div>
      ))}
    </div>
  );
};

const PERIOD_LABELS = {
  today:     'Today',
  thisWeek:  'This Week',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
};

const PER_PAGE = 10;

const CostAnalyticsModal = ({ data, loading, onClose }) => {
  const total    = data?.totalCost         || {};
  const periods  = data?.periodBreakdown   || {};
  const cats     = data?.categoryBreakdown || [];
  const campaigns = data?.campaignCosts    || [];

  const [campPage, setCampPage] = useState(1);
  const totalCampPages = Math.max(1, Math.ceil(campaigns.length / PER_PAGE));
  const pagedCampaigns = campaigns.slice((campPage - 1) * PER_PAGE, campPage * PER_PAGE);

  /* Period bar chart data */
  const periodBarData = useMemo(() =>
    Object.entries(PERIOD_LABELS).map(([key, label]) => ({
      name:      label,
      Gross:     parseFloat(periods[key]?.grossCost  || 0),
      Net:       parseFloat(periods[key]?.netCost    || 0),
      Savings:   parseFloat(periods[key]?.failedSavings || 0),
    })),
  [periods]);

  return (
    <div className="cost-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cost-modal" role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="cost-hdr">
          <div className="cost-hdr-left">
            <div className="cost-hdr-badge"><DollarSign size={16} /></div>
            <div>
              <h2 className="cost-hdr-title">Cost Analytics</h2>
              <p className="cost-hdr-sub">Campaign spending & savings breakdown</p>
            </div>
          </div>
          <button className="cost-hdr-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        {/* ── Disclaimer ── */}
        <div className="cost-disclaimer">
          <AlertTriangle size={13} className="cost-disclaimer-icon" />
          <span>The figures shown are <strong>estimated costs</strong> based on your campaign activity. Actual charges may vary slightly depending on Meta's billing cycle and applicable taxes.</span>
        </div>

        {/* ── Body ── */}
        {loading ? (
          <div className="cost-loading">
            <div className="cost-spin" />
            <p>Loading analytics…</p>
          </div>
        ) : (
          <div className="cost-body">

            {/* Section 1 — Cost Overview */}
            <div className="cost-section">
              <p className="cost-section-label">Cost Overview</p>
              <div className="cost-stat-grid">
                {[
                  { label: 'Gross Cost',      value: `₹${fmt(total.grossCost)}`,    accent: null  },
                  { label: 'Failed Savings',  value: `₹${fmt(total.failedSavings)}`, accent: TEAL  },
                  { label: 'Net Cost',        value: `₹${fmt(total.netCost)}`,       accent: BLUE  },
                  { label: 'Campaigns',       value: fmtInt(total.totalCampaigns),   accent: null  },
                  { label: 'Recipients',      value: fmtInt(total.totalRecipients),  accent: null  },
                ].map(s => (
                  <div key={s.label} className="cost-stat-card">
                    <span className="cost-stat-val" style={s.accent ? { color: s.accent } : {}}>{s.value}</span>
                    <span className="cost-stat-lbl">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 2 — Period Breakdown cards */}
            <div className="cost-section">
              <p className="cost-section-label">Period Breakdown</p>
              <div className="cost-period-grid">
                {Object.entries(PERIOD_LABELS).map(([key, label]) => {
                  const p = periods[key] || {};
                  return (
                    <div key={key} className="cost-period-card">
                      <p className="cost-period-name">{label}</p>
                      <div className="cost-period-rows">
                        <div className="cost-period-row">
                          <span className="cost-period-lbl">Gross</span>
                          <span className="cost-period-val">₹{fmt(p.grossCost)}</span>
                        </div>
                        <div className="cost-period-row">
                          <span className="cost-period-lbl">Savings</span>
                          <span className="cost-period-val" style={{ color: TEAL }}>₹{fmt(p.failedSavings)}</span>
                        </div>
                        <div className="cost-period-row">
                          <span className="cost-period-lbl">Net</span>
                          <span className="cost-period-val" style={{ color: BLUE, fontWeight: 600 }}>₹{fmt(p.netCost)}</span>
                        </div>
                        <div className="cost-period-row cost-period-row--last">
                          <span className="cost-period-lbl">Campaigns</span>
                          <span className="cost-period-val">{fmtInt(p.campaigns)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Section 3 — Period Bar Chart */}
            <div className="cost-section">
              <p className="cost-section-label">Cost by Period</p>
              <div className="cost-card cost-chart-card">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={periodBarData} barSize={18} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--cost-grid)" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--cost-muted)' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--cost-muted)' }} axisLine={false} tickLine={false} tickFormatter={v => `₹${v}`} />
                    <Tooltip content={<CostTooltip />} />
                    <Bar dataKey="Gross"   fill={AMBER} radius={[3, 3, 0, 0]} name="Gross" />
                    <Bar dataKey="Net"     fill={BLUE}  radius={[3, 3, 0, 0]} name="Net" />
                    <Bar dataKey="Savings" fill={TEAL}  radius={[3, 3, 0, 0]} name="Savings" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="cost-chart-legend">
                  {[
                    { label: 'Gross Cost',     color: AMBER },
                    { label: 'Net Cost',       color: BLUE  },
                    { label: 'Failed Savings', color: TEAL  },
                  ].map(l => (
                    <span key={l.label} className="cost-legend-item">
                      <span className="cost-legend-dot" style={{ background: l.color }} />
                      {l.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Section 4 — Category Breakdown */}
            {cats.length > 0 && (
              <div className="cost-section">
                <p className="cost-section-label">Category Breakdown</p>
                <div className="cost-card cost-table-card">
                  <table className="cost-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Campaigns</th>
                        <th>Recipients</th>
                        <th>Gross Cost</th>
                        <th>Savings</th>
                        <th>Net Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cats.map((c, i) => (
                        <tr key={i}>
                          <td><span className="cost-badge">{c.category}</span></td>
                          <td>{fmtInt(c.campaigns)}</td>
                          <td>{fmtInt(c.totalRecipients)}</td>
                          <td>₹{fmt(c.grossCost)}</td>
                          <td style={{ color: TEAL }}>₹{fmt(c.failedSavings)}</td>
                          <td style={{ color: BLUE, fontWeight: 600 }}>₹{fmt(c.netCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Section 5 — Campaign Costs Table */}
            {campaigns.length > 0 && (
              <div className="cost-section">
                <p className="cost-section-label">Campaign Costs</p>
                <div className="cost-card cost-table-card">
                  <table className="cost-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Campaign</th>
                        <th>Recipients</th>
                        <th>Failed</th>
                        <th>Gross</th>
                        <th>Savings</th>
                        <th>Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedCampaigns.map((c, i) => (
                        <tr key={c.campaignId || i}>
                          <td className="cost-tbl-num">{(campPage - 1) * PER_PAGE + i + 1}</td>
                          <td className="cost-tbl-name">{c.campaignName}</td>
                          <td>{fmtInt(c.recipients)}</td>
                          <td style={c.failedCount > 0 ? { color: RED } : {}}>{fmtInt(c.failedCount)}</td>
                          <td>₹{fmt(c.grossCost)}</td>
                          <td style={{ color: TEAL }}>₹{fmt(c.failedSavings)}</td>
                          <td style={{ color: BLUE, fontWeight: 600 }}>₹{fmt(c.netCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalCampPages > 1 && (
                  <div className="cost-pagination">
                    <span className="cost-pg-info">{campaigns.length} campaigns</span>
                    <div className="cost-pg-controls">
                      <button
                        className="cost-pg-btn"
                        disabled={campPage <= 1}
                        onClick={() => setCampPage(p => p - 1)}
                      ><ChevronLeft size={14} /></button>
                      <span className="cost-pg-text">{campPage} / {totalCampPages}</span>
                      <button
                        className="cost-pg-btn"
                        disabled={campPage >= totalCampPages}
                        onClick={() => setCampPage(p => p + 1)}
                      ><ChevronRight size={14} /></button>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default CostAnalyticsModal;
